import React from 'react';
import classNames from 'classnames';
import { motion, AnimatePresence } from 'framer-motion';
import {
  useFloating,
  offset,
  useHover,
  useFocus,
  useDismiss,
  useRole,
  useMergeRefs,
  useInteractions,
  FloatingPortal,
} from '@floating-ui/react';

import { RCIcon, RCButton } from '../';
import { FLOATING_UI_DOM_ID } from '../../constants';
import { ButtonStyling } from '../RCButton/styling';

interface IProps extends ButtonStyling {
  /**
   * Import the SVG with `?component` in the import path, and pass it through here.
   */
  svgComponent: (props: React.SVGProps<SVGElement>) => React.ReactElement;

  /** A short description of the button's action, generally 1-2 words. Rendered in a tooltip and in the 'aria-label' attribute. */
  label: string;

  /** The click handler passed to the button. */
  onClick: (e) => void,

  'aria-disabled'?: boolean;
}

/**
 * Combines `RCIcon` and `RCButton` into a fully accessible component.
 * Use whenever you need a clickable icon, with the exception of a close button - we have `RCCloseButton` for that specific case.
*/
export const RCIconButton = React.forwardRef<HTMLButtonElement, IProps>(({
  svgComponent,
  label,
  onClick: passthroughOnClick,
  inverted = false,
  variant = 'transparent',
  size = 'medium',
  ...passthroughProps
}: IProps & React.ButtonHTMLAttributes<HTMLButtonElement>, ref) => {

  const [open, setOpen] = React.useState(false);

  const { x, y, reference, floating, strategy, context } =
    useFloating({
      open,
      onOpenChange: setOpen,
      placement: 'bottom',
      middleware: [offset(5)],
    });

  const { getReferenceProps, getFloatingProps } = useInteractions([
    useFocus(context, { enabled: true, keyboardOnly: true }),
    useHover(context, { move: false, mouseOnly: true }),
    useDismiss(context, { referencePress: true, referencePressEvent: 'click' }),
    useRole(context, { role: 'tooltip' }),
  ]);

  const mergedRef = useMergeRefs([
    reference,
    ref,
  ]);

  const labelClasses = classNames(
    'rc-icon-button__label',
    { 'rc-icon-button__label--dark': !inverted },
  );

  return (
    <div className="rc-icon-button">
      <RCButton
        aria-label={label}
        inverted={inverted}
        variant={variant}
        size={size}
        ref={mergedRef}
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...passthroughProps}
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...getReferenceProps({
          onClick(e) {
            passthroughOnClick?.(e);
          },
        })}
      >
        <RCIcon svgComponent={svgComponent} />
      </RCButton>
      <FloatingPortal id={FLOATING_UI_DOM_ID}>
        <AnimatePresence>
          {open && (
            <motion.div
              ref={floating}
              className={labelClasses}
              style={{
                position: strategy,
                top: y ?? 0,
                left: x ?? 0,
                width: 'max-content',
              }}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.3, delay: 0.2 }}
              // eslint-disable-next-line react/jsx-props-no-spreading
              {...getFloatingProps()}
            >
              {label}
            </motion.div>
          )}
        </AnimatePresence>
      </FloatingPortal>
    </div>
  );
});

// Explicitly setting displayName to help with debugging and tracing an otherwise anonymous function.
RCIconButton.displayName = 'RCIconButton';
